import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import type { TableLazyLoadEvent } from 'primeng/types/table';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, catchError, debounceTime, distinctUntilChanged, of } from 'rxjs';
import { UserAdminService } from '../../../core/services/user-admin.service';
import { AuthService } from '../../../core/services/auth.service';
import { AdminUserResponse, PageResponse } from '../../../core/models';
import { AppEmptyState } from '../../../shared/ui/empty-state/app-empty-state';
import { AppStatusTag, StatusTagSeverity } from '../../../shared/ui/status-tag/app-status-tag';
import { UserFormComponent } from '../user-form/user-form';
import { SystemUserFormComponent } from '../system-user-form/system-user-form';
import { UserPermissionsComponent } from '../user-permissions/user-permissions';

const EMPTY_PAGE: PageResponse<AdminUserResponse> = { content: [], totalElements: 0, totalPages: 0, number: 0, size: 20 };

const ROLE_OPTIONS = [
  { label: 'Tous les rôles',          value: '' },
  { label: 'Super administrateur',    value: 'ROLE_SUPER_ADMIN' },
  { label: 'Administrateur',          value: 'ROLE_ADMIN' },
  { label: 'Utilisateur (particulier)', value: 'ROLE_USER' },
  { label: 'Agent immobilier',        value: 'ROLE_AGENT' },
  { label: 'Locataire',               value: 'ROLE_TENANT' },
];

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, TableModule,
    InputTextModule, SelectModule,
    ProgressSpinnerModule, TooltipModule, ConfirmDialogModule, ToastModule,
    AppEmptyState, AppStatusTag, UserFormComponent, SystemUserFormComponent, UserPermissionsComponent],
  templateUrl: './user-list.html',
  styleUrl: './user-list.scss'
})
export class UserListComponent implements OnInit, OnDestroy {
  private svc     = inject(UserAdminService);
  private confirm = inject(ConfirmationService);
  private toast   = inject(MessageService);
  authService     = inject(AuthService);

  readonly roleOptions = ROLE_OPTIONS;

  // ── Liste + filtres ────────────────────────────────────────────────────────
  loading   = signal(true);
  page      = signal<PageResponse<AdminUserResponse>>(EMPTY_PAGE);
  searchTerm = signal('');
  roleFilter = signal('');
  toggling   = signal<string | null>(null);

  private pageSize  = 20;
  private pageIndex = 0;
  private search$ = new Subject<string>();

  // ── Dialogue de création ──────────────────────────────────────────────────
  createDialogVisible = signal(false);
  systemCreateDialogVisible = signal(false);

  // ── Dialogue de permissions ─────────────────────────────────────────────
  permissionsDialogVisible = signal(false);
  permissionsUser = signal<AdminUserResponse | null>(null);

  ngOnInit() {
    this.load();
    this.search$.pipe(debounceTime(400), distinctUntilChanged()).subscribe(term => {
      this.searchTerm.set(term);
      this.pageIndex = 0;
      this.load();
    });
  }

  ngOnDestroy() {
    this.search$.complete();
  }

  // ── Chargement ───────────────────────────────────────────────────────────

  load() {
    this.loading.set(true);
    this.svc.getUsers(this.pageIndex, this.pageSize, this.searchTerm() || undefined, this.roleFilter() || undefined)
      .pipe(catchError(() => of(null)))
      .subscribe(res => {
        if (res) this.page.set(res);
        this.loading.set(false);
      });
  }

  onLazyLoad(e: TableLazyLoadEvent) {
    const rows = e.rows ?? this.pageSize;
    this.pageSize = rows;
    this.pageIndex = Math.floor((e.first ?? 0) / rows);
    this.load();
  }

  onSearchChange(value: string) {
    this.search$.next(value);
  }

  onRoleFilterChange(value: string) {
    this.roleFilter.set(value);
    this.pageIndex = 0;
    this.load();
  }

  // ── Verrouillage / déverrouillage ───────────────────────────────────────

  confirmToggleLock(user: AdminUserResponse, event: Event) {
    const willLock = !user.locked;
    this.confirm.confirm({
      target: event.target as EventTarget,
      message: willLock
        ? `Verrouiller le compte de « ${user.firstName} ${user.lastName} » ? L'utilisateur ne pourra plus se connecter.`
        : `Déverrouiller le compte de « ${user.firstName} ${user.lastName} » ?`,
      header: willLock ? 'Confirmer le verrouillage' : 'Confirmer le déverrouillage',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: willLock ? 'Verrouiller' : 'Déverrouiller',
      rejectLabel: 'Annuler',
      acceptButtonProps: { severity: willLock ? 'danger' : 'success' },
      rejectButtonProps: { severity: 'secondary', outlined: true },
      accept: () => this.toggleLock(user)
    });
  }

  private toggleLock(user: AdminUserResponse) {
    this.toggling.set(user.id);
    this.svc.toggleLock(user.id).pipe(catchError(() => of(null))).subscribe(res => {
      this.toggling.set(null);
      if (res) {
        this.page.update(p => ({ ...p, content: p.content.map(u => u.id === res.id ? res : u) }));
        this.toast.add({
          severity: 'success',
          summary: res.locked ? 'Compte verrouillé' : 'Compte déverrouillé',
          detail: `« ${res.firstName} ${res.lastName} »`,
          life: 4000
        });
      } else {
        this.toast.add({ severity: 'error', summary: 'Erreur', detail: "L'opération a échoué.", life: 5000 });
      }
    });
  }

  // ── Création ─────────────────────────────────────────────────────────────

  openCreateDialog() {
    this.createDialogVisible.set(true);
  }

  openSystemCreateDialog() {
    this.systemCreateDialogVisible.set(true);
  }

  onUserCreated(user: AdminUserResponse) {
    this.page.update(p => ({ ...p, content: [user, ...p.content], totalElements: p.totalElements + 1 }));
  }

  // ── Permissions ──────────────────────────────────────────────────────────

  openPermissionsDialog(user: AdminUserResponse) {
    this.permissionsUser.set(user);
    this.permissionsDialogVisible.set(true);
  }

  // ── Formatage ────────────────────────────────────────────────────────────

  roleLabel(role: string): string {
    return this.roleOptions.find(r => r.value === role)?.label ?? role;
  }

  roleSeverity(role: string): StatusTagSeverity {
    const map: Record<string, StatusTagSeverity> = {
      ROLE_SUPER_ADMIN: 'contrast',
      ROLE_ADMIN:       'danger',
      ROLE_AGENT:       'info',
      ROLE_USER:        'success',
      ROLE_TENANT:      'secondary',
    };
    return map[role] ?? 'secondary';
  }

  accountTypeLabel(type: string): string {
    if (type === 'PROFESSIONNEL') return 'Professionnel';
    if (type === 'SYSTEM') return 'Système';
    return 'Particulier';
  }

  statusLabel(user: AdminUserResponse): string {
    if (user.locked) return 'Verrouillé';
    return user.enabled ? 'Actif' : 'Inactif';
  }

  statusSeverity(user: AdminUserResponse): StatusTagSeverity {
    if (user.locked) return 'danger';
    return user.enabled ? 'success' : 'secondary';
  }

  formatDate(d: string) { return d ? new Date(d).toLocaleDateString('fr-FR') : '—'; }
}
